import React, { useCallback, useEffect, useState } from 'react';
import { Box, Image } from 'grommet';
import DrawingPanel from '../Drawing/DrawingPanel';
import { customModes, MAP_MODES } from '../Drawing/modes/modes';
import drawStyles from './drawStyles';
import AgoroGeoJSONList from '../GeoJSON/AgoroGeoJSONList';
import { useNavigate, useParams } from 'react-router-dom';
import DrawControl from './DrawControl';
import MapV from 'react-map-gl';
import * as turf from '@turf/turf';
import 'mapbox-gl/dist/mapbox-gl.css';
import { DeleteField } from '../Modals/DeleteField';
import { AddressSearchComponent } from '../Search/AddressSearchComponent';
import mapboxgl from 'mapbox-gl';
import { UploadModal } from '../Modals/UploadModal';
import Wizard from '../Wizard/Wizard';
import InformationPanel from '../InformationPanel/InformationPanel';
import arrowBack from '../../../../assets/BoundaryTool/arrow-back.svg';
import { navigateBackToProfile, getPaddedBBox, allowBoundaryEditAndDelete } from '../../service/common/utils';
import ConfirmationModal from '../Modals/ConfirmationModal';
import { CoordinateSearchComponent } from '../Search/CoordinateSearchComponent';
import { useTranslation } from 'react-i18next';
import { ShowCloserFieldsComponents } from '../Search/ShowCloserFieldsComponent';
import { FormDown, FormUp } from 'grommet-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useDispatch } from 'react-redux';
import { mapLoaded, onDrawModeChange, onDrawUpdate, restartDraw, setDrawControl } from '../../../../redux/actions/map-actions/map-action';
import { removeFields, restartPaddock, startEditingField, startSplittingField } from '../../../../redux/actions/map-actions/field/field-action';
import { Feature } from '@turf/helpers';
import { fitBoundsOption } from '../../../../redux/actions/map-actions/field/field-helper';
import { useAppSelector } from '../../../../redux';
import { IProducer } from '../../../producer-list/interfaces';
import { ClassificationChart } from '../classification/ClassificationChart';
import { CDLStyling, classificationStyling, DWStyling, ESAStyling, LayersOverrite } from '../../../../service/common/constants';
import { LayerSelector } from '../classification/LayerSelector';

const addLayerToMap = (map, id: string, type: any, source: any, paint: any, layout: any) => {
  if (!map) {
    return;
  }
  if (!map?.getLayer(id)) {
    const layer: any = {
      id: id,
      type: type,
      source: source,
    };
    if (paint) {
      layer.paint = paint;
    }
    if (layout) {
      layer.layout = layout;
    }
    map?.addLayer(layer);
  }
};

const addSourceToMap = (map, id: string, showLabel, areaAttribute, overrite: LayersOverrite = {}) => {
  if (!map) {
    return;
  }

  const fillPaint = overrite.fillPaint ?? {
    'fill-color': ['get', 'fillColor'],
    'fill-opacity': ['get', 'fillOpacity'],
  };
  // map.removeSource(id);
  if (!map?.getSource(id)) {
    map?.addSource(id, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }
  addLayerToMap(
    map,
    `${id}-outline`,
    'line',
    id,
    {
      'line-color': ['get', 'lineColor'],
      'line-width': ['get', 'lineWidth'],
    },
    null,
  );

  addLayerToMap(map, `${id}-fill`, 'fill', id, fillPaint, null);

  if (showLabel) {
    addLayerToMap(
      map,
      `${id}-label`,
      'symbol',
      id,
      {
        'text-color': '#fff',
        'text-halo-color': '#282828',
        'text-halo-width': 2.5,
      },
      {
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          2,
          ['max', ['/', ['ln', ['get', areaAttribute]], ['ln', 10000]], 0],
          10,
          ['max', ['/', ['ln', ['get', areaAttribute]], ['ln', 1.8]], 0],
          14,
          ['+', 2, ['max', ['/', ['ln', ['get', areaAttribute]], ['ln', 1.2]], 0]],
          16,
          ['+', 4, ['max', ['/', ['ln', ['get', areaAttribute]], ['ln', 1.1]], 0]],
        ],
      },
    );
  }
};

export const AgoroMap = () => {
  const wizardStep: 'FIELD' | 'PADDOCK' = 'FIELD'; //TODO fixed this as FIELD since grazing is disabled

  const { t, i18n } = useTranslation();
  const { journeyId } = useParams();
  const dispatch = useDispatch();
  const [showBackModal, setShowBackModal] = useState<boolean>(false);
  const [panelClosed, setPanelClosed] = useState<boolean>(false);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
  const [fieldToDelete, setFieldToDelete] = useState<Feature | boolean | undefined>(undefined);
  const navigate = useNavigate();

  const { cpBoundaryTool, shouldLdAllowBoundaryEdit } = useFlags();

  const isChannelPartner = useAppSelector((state) => !!state.user.userData.channelPartnerId);
  const map = useAppSelector((state) => state.map.map);
  const isDrawingTraining = useAppSelector((state) => state.map.isDrawingTraining);
  const fields = useAppSelector((state) => state.map.fields);
  const mode = useAppSelector((state) => state.map.mode);
  const state = useAppSelector((state) => state.map.state);
  const loading = useAppSelector((state) => state.map.loadingFields);
  const drawControl = useAppSelector((state) => state.map.drawControl);
  const willLoseProgressOnExit = useAppSelector((state) => state.map.updatedFieldBoundaries);
  const country = useAppSelector((state) => state.producer.producerInformation?.country ?? 'us');

  const producersWithGroups: IProducer[] = useAppSelector((state) => state.producer.processedProducerList);

  const allowBoundaryEdit = allowBoundaryEditAndDelete(shouldLdAllowBoundaryEdit, producersWithGroups, journeyId);

  if (isChannelPartner && !cpBoundaryTool) {
    navigateBackToProfile(journeyId || '', navigate, dispatch);
  }

  const updateMapFromAddress = (bbox: number[], map: mapboxgl.Map) => {
    if (bbox && bbox.length === 4) {
      map.fitBounds([bbox[0], bbox[1], bbox[2], bbox[3]], fitBoundsOption);
    } else if (bbox && bbox.length === 2) {
      map.fitBounds([bbox[0], bbox[1]], fitBoundsOption);
    }
  };

  const changeMapPosition = React.useCallback(
    (bbox: number[]) => {
      updateMapFromAddress(bbox, map!);
    },
    [map],
  );

  const showDrawingTools = useCallback(() => {
    const isSetup = state === 'FIRST_SETUP' || state === 'SECOND_SETUP';
    return isSetup && (wizardStep === 'FIELD' || (wizardStep === 'PADDOCK' && (mode === MAP_MODES.SPLIT_FIELD || mode === MAP_MODES.PASSING_MODE)));
  }, [state, wizardStep, mode]);

  useEffect(() => {
    if (!map) {
      return;
    }
    map.setPaintProperty('fields-fill', 'fill-opacity', isDrawingTraining ? 0 : 0.5);
    // map.moveLayer('fields');

    // map.moveLayer('fields-label');
  }, [isDrawingTraining]);

  useEffect(() => {
    if (!map) {
      return;
    }
    console.log('refaz');
    addSourceToMap(map, 'wetlands', true, 'area');
    addSourceToMap(map, 'public-lands', true, 'area');
    addSourceToMap(map, 'near-fields', false, null);
    addSourceToMap(map, 'paddocks', true, 'area');
    addSourceToMap(map, 'fields', true, 'area');
    addSourceToMap(map, 'fields_', true, 'area');
    // classification layers
    addSourceToMap(map, 'cdl_landcover', false, null, CDLStyling);
    addSourceToMap(map, 'classified_dw', false, null, DWStyling);
    addSourceToMap(map, 'esa_landcover', false, null, ESAStyling);
    addSourceToMap(map, 'hull-Tree-clustered', false, null, {
      fillPaint: {
        'fill-color': '#00ff50',
        'fill-opacity': 0.6,
      },
    });
    addSourceToMap(map, 'classified', false, null, classificationStyling);
    addSourceToMap(map, 'training-regions', false, null, classificationStyling);

    map.moveLayer('fields-label');
    // move label to top
  }, [map]);

  return (
    <Box>
      {showConfirmationModal && (
        <ConfirmationModal
          dataCy={'delete-all-paddocks-modal'}
          confirm={() => {
            setShowConfirmationModal(false);
            dispatch(restartDraw());
            dispatch(restartPaddock());
            dispatch(startSplittingField(fields.find((field: Feature) => field.properties?.['editing'])));
          }}
          cancel={() => setShowConfirmationModal(false)}
          message={t('fieldFlow.deletePaddocks.text')}
        />
      )}
      {showBackModal && (
        <ConfirmationModal
          dataCy={'leave-field-flow-modal'}
          confirm={() => {
            if (journeyId) navigateBackToProfile(journeyId, navigate, dispatch);
          }}
          cancel={() => setShowBackModal(false)}
          title={t('fieldFlow.leaveModal.title')}
          message={t('fieldFlow.leaveModal.text')}
          confirmLabel={t('fieldFlow.leaveModal.confirm')}
          cancelLabel={t('fieldFlow.leaveModal.cancel')}
        />
      )}
      {showUploadModal && (
        <UploadModal
          journeyId={journeyId ?? ''}
          country={country}
          onContinue={() => {
            if (fields.length) {
              const newCenter = getPaddedBBox(
                turf.bbox(turf.featureCollection(fields.map((f) => f.properties?.processedIneligibleBoundaries?.original.boundaries ?? f))),
              );
              map?.fitBounds([newCenter[0], newCenter[1], newCenter[2], newCenter[3]], fitBoundsOption);
            }
            setShowUploadModal(false);
          }}
          onBack={() => {
            setShowUploadModal(false);
          }}
        />
      )}
      {fieldToDelete !== undefined && (
        <DeleteField
          deleteAllFields={fieldToDelete === true}
          onCancel={() => {
            setFieldToDelete(undefined);
          }}
          onDelete={() => {
            dispatch(removeFields(typeof fieldToDelete === 'object' ? fieldToDelete : undefined));
            setFieldToDelete(undefined);
          }}
        />
      )}
      {/*todo fix map size*/}
      <MapV
        initialViewState={{
          bounds:
            i18n.language !== 'pt-BR'
              ? [-171.791110603, 18.91619, -66.96466, 71.3577635769]
              : [-82.4117709445771, -44.60369527891214, 14.628196910351932, 17.219142546755293],
        }}
        mapboxAccessToken={'' + process.env['REACT_APP_MAP_BOX_KEY']}
        mapStyle='mapbox://styles/mapbox/satellite-streets-v11?optimize=true'
        style={{ height: '100vh', width: '100%', position: 'fixed' }}
        interactiveLayerIds={state !== 'FIRST_SETUP' && state !== 'SECOND_SETUP' ? [] : ['fields-fill']}
        onClick={(e) => {
          if (!wizardStep || !allowBoundaryEdit || isDrawingTraining) {
            return;
          }
          const f = map?.queryRenderedFeatures(e.point, { layers: ['fields-fill'] });
          const fieldId = f?.length ? f[0].properties?.parent : '';
          if (!fields.find((i) => i.id === fieldId)) {
            // near field
            return;
          }
          // if (wizardStep === 'PADDOCK') {
          //   dispatch(startSplittingField(fieldId));
          // } else {
          document.getElementById(`field-${fieldId}`)?.scrollIntoView({ behavior: 'smooth' });
          dispatch(startEditingField(fieldId));
          // }
        }}
        onLoad={(e) => {
          dispatch(mapLoaded(e.target as mapboxgl.Map));
        }}>
        <AgoroGeoJSONList />
      </MapV>
      {map && (
        <>
          <DrawControl
            map={map}
            ref={(draw) => {
              if (drawControl || !draw) {
                return;
              }
              dispatch(setDrawControl(draw));
            }}
            defaultMode={MAP_MODES.STATIC}
            displayControlsDefault={false}
            modes={customModes}
            styles={drawStyles}
            onDrawUpdate={(event) => dispatch(onDrawUpdate(event))}
            onDrawModeChange={(event) => dispatch(onDrawModeChange(event.mode))}
          />
        </>
      )}

      {map && (
        <>
          <Box
            width={'fit-content'}
            style={{
              position: 'absolute',
              zIndex: 11,
              top: '24px',
              left: '24px',
            }}>
            <Box
              direction={'row'}
              gap={'16px'}
              style={{
                position: 'relative',
              }}>
              <Box
                width={'48px'}
                height={'48px'}
                justify={'center'}
                align={'center'}
                round={'5px'}
                data-cy={'back-to-profile'}
                onClick={() => {
                  if (willLoseProgressOnExit) setShowBackModal(true);
                  else if (journeyId) navigateBackToProfile(journeyId, navigate, dispatch);
                }}
                border={{ color: '#808080', size: '1px' }}
                background={'#ffffff'}>
                <Image src={arrowBack} width={'24px'} height={'24px'} />
              </Box>
              <Box
                width={'20vw'}
                height={'48px'}
                round={'5px'}
                style={{
                  maxWidth: '280px',
                  background: '#ffffff',
                }}>
                <AddressSearchComponent addressSelectedBBoxCallback={changeMapPosition} country={country} />
              </Box>
              <CoordinateSearchComponent map={map} />
              {state !== 'OVERVIEW_V2' && !isChannelPartner && <ShowCloserFieldsComponents />}
              <LayerSelector />
            </Box>
          </Box>
          {showDrawingTools() && (
            <Box
              id={'box-drawing-tool'}
              direction={'row'}
              width={'calc(60vw - 24px)'}
              justify={'start'}
              wrap={true}
              align={'end'}
              style={{
                pointerEvents: 'none',
                position: 'absolute',
                bottom: '24px',
                minWidth: 'calc(100vw - 824px)',
                maxWidth: 'calc(100vw - 584px)',
                left: panelClosed ? `calc(50% - ${Number(document.getElementById('box-drawing-tool')?.offsetWidth) / 2}px)` : '24px',
                zIndex: 11,
              }}>
              <ClassificationChart />

              <Box margin={'0 auto'} height={'min-content'} style={{ marginTop: '1rem', paddingRight: '1.5rem' }}>
                <DrawingPanel
                  setShowUploadModal={setShowUploadModal}
                  setShowConfirmationModal={setShowConfirmationModal}
                  allowBoundaryEdit={allowBoundaryEdit}
                />
              </Box>
            </Box>
          )}
        </>
      )}

      {(state === 'FIRST_SETUP' || state === 'SECOND_SETUP') && !panelClosed && <Wizard setFieldToDelete={setFieldToDelete} />}
      {(state === 'OVERVIEW' || state === 'OVERVIEW_V2' || state === 'DETAILS') && !panelClosed && <InformationPanel />}

      {!loading && (
        <Box
          background={'#fff'}
          data-cy={'toggle-fields-list'}
          style={{
            position: 'absolute',
            right: panelClosed ? '24px' : '48px',
            top: panelClosed ? '24px' : '48px',
            zIndex: 1,
            padding: '24px',
            borderRadius: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            border: '1px solid rgb(40, 40, 40)',
          }}
          onClick={() => setPanelClosed(!panelClosed)}>
          {panelClosed && <FormDown color='#282828' />}
          {!panelClosed && <FormUp color='#282828' />}
        </Box>
      )}
    </Box>
  );
};
